import { cn } from "@omni/utils/styles";
import { useEffect, useRef, useState } from "react";
import { Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperCore } from "swiper/types";

import { CategorySliderConfig } from "@/components/Product/Categories/categorySlider.config";
import { NavButton } from "@/components/Product/Categories/components/navButton/navButton";
import * as SwiperUtils from "@/utils/swiper.utils";

export type Category = {
    path: string;
    altText: string;
    link: string;
    isTargetBlank: boolean;
};

export type CategorySliderProps = {
    categories: Array<Category>;
};

const CategorySlider = ({ categories = [] }: CategorySliderProps) => {
    const [allowPrevious, setAllowPrevious] = useState(false);
    const [allowNext, setAllowNext] = useState(true);
    const [mainSwiper, setMainSwiper] = useState<SwiperCore>();
    const navigationPrevRef = useRef(null);
    const navigationNextRef = useRef(null);

    const variables = SwiperUtils.getLayoutCssVariables(CategorySliderConfig);

    const breakPointSettings = SwiperUtils.getEffectiveLayoutSettings(
        CategorySliderConfig.layoutSettings
    );

    const updateButtons = () => {
        if (mainSwiper) {
            setAllowPrevious(!mainSwiper.isBeginning);
            setAllowNext(!mainSwiper.isEnd);
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        updateButtons();
    }, [!!mainSwiper]);

    return (
        <div className={cn("tw:flex")}>
            <NavButton
                className={cn("tw:my-auto tw:mx-0 tw:hidden tw:md:block")}
                innerRef={navigationPrevRef}
                disabled={!allowPrevious}
                icon={"left"}
                onClick={() => {
                    if (allowPrevious && mainSwiper) {
                        mainSwiper.slidePrev();
                    }
                }}
            />
            <Swiper
                modules={[Navigation]}
                updateOnWindowResize={true}
                navigation={{
                    nextEl: navigationNextRef.current,
                    prevEl: navigationPrevRef.current
                }}
                onSlideChange={() => updateButtons()}
                onSwiper={setMainSwiper}
                breakpoints={breakPointSettings}
                spaceBetween={CategorySliderConfig.spaceBetween}
                className={cn("tw:w-full tw:grow", {
                    "tw:[--slides-per-view:1]": !mainSwiper,
                    "tw:xs:[--slides-per-view:var(--slides-xs)]": !mainSwiper,
                    "tw:sm:[--slides-per-view:var(--slides-sm)]": !mainSwiper,
                    "tw:md:[--slides-per-view:var(--slides-md)]": !mainSwiper,
                    "tw:lg:[--slides-per-view:var(--slides-lg)]": !mainSwiper,
                    "tw:xl:[--slides-per-view:var(--slides-xl)]": !mainSwiper
                })}
                style={variables}
            >
                {categories.map((category) => (
                    <SwiperSlide
                        key={category.link}
                        className={cn("tw:select-none", {
                            "tw:w-[calc((100%/var(--slides-per-view))-var(--slider-spacing)+(var(--slider-spacing)/(var(--slides-per-view))))] tw:mr-(--slider-spacing)":
                                !mainSwiper
                        })}
                    >
                        <a
                            className={cn("tw:block")}
                            href={category.link}
                            target={category.isTargetBlank ? "_blank" : ""}
                            rel={
                                category.isTargetBlank
                                    ? "noopener noreferrer"
                                    : ""
                            }
                        >
                            <div
                                className={cn(
                                    "tw:w-full tw:max-md:h-0 tw:max-md:overflow-hidden tw:max-md:pt-[75%] tw:max-md:relative"
                                )}
                            >
                                <div
                                    className={cn(
                                        "tw:max-md:rounded-full tw:max-md:overflow-hidden tw:max-md:my-0 tw:max-md:mx-[12.5%] tw:max-md:pt-[12.5%] tw:max-md:bg-[#e0f3ff] tw:max-md:absolute tw:max-md:top-0 tw:max-md:h-full",
                                        {
                                            "tw:md:aspect-4/3": !mainSwiper
                                        }
                                    )}
                                >
                                    <img
                                        className={cn(
                                            "tw:max-md:max-w-full tw:max-md:w-full tw:max-md:h-auto"
                                        )}
                                        alt={category.altText}
                                        src={category.path}
                                        loading={"lazy"}
                                        decoding={"async"}
                                        width={"auto"}
                                        height={"auto"}
                                    />
                                </div>
                            </div>
                            <p
                                className={cn(
                                    "tw:text-center tw:mt-2 tw:text-primary",
                                    "tw:focus:underline",
                                    "tw:active:underline"
                                )}
                            >
                                {category.altText}
                            </p>
                        </a>
                    </SwiperSlide>
                ))}
            </Swiper>
            <NavButton
                className={cn("tw:my-auto tw:mx-0 tw:hidden tw:md:block")}
                innerRef={navigationNextRef}
                disabled={!allowNext}
                icon={"right"}
                onClick={() => {
                    if (allowNext && mainSwiper) {
                        mainSwiper.slideNext();
                    }
                }}
            />
        </div>
    );
};

export default CategorySlider;
